import { AuthApiS } from '@/features/Auth/outbound/share';
import { FileApiS, FileConstantS, FileErrorS } from '@/features/File/outbound/share';
import { isFeatureError } from '@/libs/errors/FeatureError';
import { T } from '@/libs/translation';
import { resizeS3Image } from '@/libs/util/resizeS3Image';
import { Icon, Loader, Typo } from '@/ui/component/atom';
import { Toast } from '@/ui/component/cell';
import { ChangeProfileImageEvent } from '@/ui/component/event';
import { getSharedData } from '@/ui/util';
import { mauth } from '@marpple/auth';
import { isAxiosError } from 'axios';
import { $, html, View } from 'rune-ts';
import { makeErrorPopup } from '../Popup/makeErrorPopup';
import { makePopup } from '../Popup/makePopup';
import klass from './ProfileImage.module.scss';

export interface ProfileImageType {
  profile_image?: string | ArrayBuffer | null;
  name?: string | null;
}

export class ProfileImage extends View<ProfileImageType> {
  private _avatar_url =
    'https://s3.marpple.co/files/u_3040850/2024/9/original/f0be1ea3164eb2479c15fc8cb3c868105c33d0cb1.png';

  private _dark_avatar_url =
    'https://s3.marpple.co/files/u_2283830/2025/3/original/8e72535a8df4d907296e344f411a4be94db14d611.png';

  get avatar_url() {
    const { theme } = getSharedData(this);
    return theme === 'Ciety' ? this._dark_avatar_url : this._avatar_url;
  }

  get imageElement() {
    const image_el = this.element().querySelector('img')!;
    return image_el;
  }

  set imageSrc(src) {
    const image_el = this.element().querySelector('img')!;
    image_el.src = src ? src : this.avatar_url;
    this.data.profile_image = src ? src : '';
  }

  private setButtonText = (text: string) => {
    this.element().querySelector(`.${klass.image_add}`)!.querySelector('span')!.textContent = text;
  };

  private showRemoveButton = () => {
    this.element().querySelector(`.${klass.image_remove}`)!.classList.remove(klass.hidden);
  };

  private hiddenRemoveButton = () => {
    this.element().querySelector(`.${klass.image_remove}`)!.classList.add(klass.hidden);
  };

  override template() {
    const { profile_image } = this.data;

    return html`<div class="${klass.image_preview}">
      <button type="button" class="${klass.image_remove} ${klass.hidden}">${new Icon({ name: 'close' })}</button>
      <button type="button" class="${klass.image_add}">
        ${new Typo(
          {
            text: profile_image ? T('mypage.button.001') : T('mypage.button.002'),
          },
          { typo: 'p3' },
        )}
      </button>
      <img src="${this.avatar_url}" loading="lazy" alt="profile image" />
      <input type="file" name="file" style="display: none" accept=".jpg, .jpeg, .png" />
    </div>`;
  }

  override onRender() {
    this.imageSrc = this.data.profile_image;

    this.imageElement.addEventListener('error', (e) => {
      this.imageSrc = '';
      this.hiddenRemoveButton();
      this.setButtonText(T('mypage.button.002'));
    });

    this.imageElement.addEventListener('load', () => {
      if (this.data.profile_image !== '') {
        this.showRemoveButton();
        this.setButtonText(T('mypage.button.001'));
      }
    });

    // 프로필 이미지 등록 버튼 클릭
    this.delegate('click', `.${klass.image_add}`, (e) => {
      $(this.element())?.find('input[name="file"]')?.element().click();
    });

    // 프로필 이미지 등록
    this.delegate('change', 'input[name="file"]', async (e) => {
      const input = e.currentTarget as HTMLInputElement;
      try {
        const is_image_existing = !!this.data.profile_image;
        Loader.start();
        const file = (e.currentTarget as HTMLInputElement)?.files?.[0];

        if (file) {
          if (file.size > FileConstantS.FILE_LIMIT) {
            Toast.open({ label: T('mypage.toast.message.file_limit'), close_label: T('common.close') }, {});
            Loader.stop();
            input.value = '';
            return;
          }
          const upload_res = await FileApiS.FileUploadApi(file);
          const profile_image = upload_res.data.url;

          await AuthApiS.postChangeProfile({ profile_image });
          await mauth.refreshUser();
          this.imageSrc = resizeS3Image({ url: profile_image, width: 320, format: 'avif' });

          const loadImage = (e) => {
            Toast.open(
              {
                label: is_image_existing ? T('mypage.profile.toast.002') : T('mypage.profile.toast.001'),
                close_label: T('common.close'),
              },
              {},
            );
            this.dispatchEvent(ChangeProfileImageEvent, {
              bubbles: true,
              detail: { file_url: this.imageElement.src },
            });
            this.showRemoveButton();
            this.setButtonText(T('mypage.button.001'));
            this.imageElement.removeEventListener('load', loadImage);

            Loader.stop();
          };
          this.imageElement.addEventListener('load', loadImage);
          this.imageElement.addEventListener('error', (e) => {
            this.imageSrc = '';
            this.hiddenRemoveButton();
            this.setButtonText(T('mypage.button.002'));
          });
        }
      } catch (e) {
        Loader.stop();
        input.value = '';
        if (isAxiosError(e)) {
          if (e.response?.data.code === 'LIMIT_FILE_SIZE') {
            Toast.open({ label: T('mypage.toast.message.file_limit'), close_label: T('common.close') }, {});
            return;
          }

          if (e.response?.data.code === FileErrorS.FILE_001.name) {
            Toast.open({ label: T('mypage.toast.message.invalid_format'), close_label: T('common.close') }, {});
            return;
          }

          return makeErrorPopup(e, e.response?.data);
        }

        if (isFeatureError(e)) {
          return makeErrorPopup(e, e.json);
        }

        return makeErrorPopup(e);
      }
    });

    // 프로필 이미지 삭제
    this.delegate('click', `.${klass.image_remove}`, async (e) => {
      const res = await makePopup({
        title: T('mypage.profile.popup.title.001'),
        message: T('mypage.profile.popup.message.001'),
        ok: T('mypage.profile.button.001'),
        cancel: T('mypage.profile.button.002'),
      });

      if (res === 'confirm') {
        await AuthApiS.postChangeProfile({ profile_image: null });
        ($(this.element()).find('input[name="file"]')!.element() as HTMLInputElement).value = '';
        await mauth.refreshUser();
        this.imageSrc = '';

        Toast.open({ label: T('mypage.profile.toast.002'), close_label: T('common.close') }, {});
        this.dispatchEvent(ChangeProfileImageEvent, {
          bubbles: true,
          detail: { file_url: '' },
        });
        this.setButtonText(T('mypage.button.002'));
        this.hiddenRemoveButton();
      }
    });
  }
}
